import { isAuthApiError } from "@supabase/auth-js";
import { z } from "zod";

import { ceremonyOutcome, codeOutcome, verifyTotp } from "./ceremony.ts";

import type { CeremonyOutcome, CodeOutcome } from "./ceremony.ts";
import type { ApiClient } from "./client.ts";

const passkeySchema = z
  .object({
    id: z.string().min(1),
    friendly_name: z.string().optional(),
    created_at: z.string(),
    last_used_at: z.string().nullish(),
  })
  .transform((row) => ({
    id: row.id,
    name: row.friendly_name ?? "",
    createdAt: row.created_at,
    lastUsedAt: row.last_used_at ?? null,
  }));

export type Passkey = z.infer<typeof passkeySchema>;

export interface CodeEnrolment {
  readonly factorId: string;
  readonly qrCode: string;
  readonly secret: string;
}

export type NeedsCode<Outcome> = Outcome | "needsCode";

export interface Factors {
  readonly passkeys: () => Promise<readonly Passkey[]>;
  readonly addPasskey: (name: string) => Promise<NeedsCode<CeremonyOutcome>>;
  readonly renamePasskey: (id: string, name: string) => Promise<void>;
  readonly removePasskey: (id: string) => Promise<NeedsCode<"done">>;
  readonly startCode: () => Promise<NeedsCode<CodeEnrolment>>;
  readonly confirmCode: (enrolment: CodeEnrolment, code: string) => Promise<CodeOutcome>;
  readonly cancelCode: (enrolment: CodeEnrolment) => Promise<void>;
}

const isBelowLevel = (error: Error | null): boolean =>
  isAuthApiError(error) && error.code === "insufficient_aal";

const orThrow = (error: Error | null): void => {
  if (error !== null) {
    throw error;
  }
};

const removeOtherCodes = async (client: ApiClient, keep: string): Promise<void> => {
  const { data, error } = await client.auth.mfa.listFactors();
  orThrow(error);
  const others = (data?.all ?? []).filter((factor) => factor.factor_type === "totp" && factor.id !== keep);
  for (const factor of others) {
    // eslint-disable-next-line no-await-in-loop -- GoTrue recomputes the session level after each unenrolment.
    const { error: unenrolError } = await client.auth.mfa.unenroll({ factorId: factor.id });
    orThrow(unenrolError);
  }
};

export const createFactors = (client: ApiClient): Factors => ({
  passkeys: async () => {
    const { data, error } = await client.auth.passkey.list();
    orThrow(error);
    return z.array(passkeySchema).parse(data);
  },
  addPasskey: async (name) => {
    const { data, error } = await client.auth.registerPasskey();
    if (isBelowLevel(error)) {
      return "needsCode";
    }
    const outcome = ceremonyOutcome(error);
    if (outcome === "done" && data !== null) {
      const { error: renameError } = await client.auth.passkey.update({
        passkeyId: data.id,
        friendlyName: name,
      });
      orThrow(renameError);
    }
    return outcome;
  },
  renamePasskey: async (id, name) => {
    const { error } = await client.auth.passkey.update({ passkeyId: id, friendlyName: name });
    orThrow(error);
  },
  removePasskey: async (id) => {
    const { error } = await client.auth.passkey.delete({ passkeyId: id });
    if (isBelowLevel(error)) {
      return "needsCode";
    }
    orThrow(error);
    return "done";
  },
  startCode: async () => {
    const { data, error } = await client.auth.mfa.enroll({
      factorType: "totp",
      friendlyName: `authenticator-${crypto.randomUUID()}`,
    });
    if (isBelowLevel(error)) {
      return "needsCode";
    }
    orThrow(error);
    if (data === null) {
      throw new Error("Enrolment returned no factor");
    }
    return { factorId: data.id, qrCode: data.totp.qr_code, secret: data.totp.secret };
  },
  confirmCode: async (enrolment, code) => {
    const outcome = codeOutcome(await verifyTotp(client.auth.mfa, enrolment.factorId, code));
    if (outcome === "done") {
      await removeOtherCodes(client, enrolment.factorId);
    }
    return outcome;
  },
  cancelCode: async (enrolment) => {
    const { error } = await client.auth.mfa.unenroll({ factorId: enrolment.factorId });
    // WHY: an unverified factor grants nothing and the next enrolment replaces it, so a failed clean-up must not block going back.
    if (error !== null) {
      reportError(error);
    }
  },
});
